import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { v4 as uuid } from 'uuid';

export const CORRELATION_ID_HEADER = 'x-correlation-id';

export const newCorrelationId = () => uuid();

/**
 * Sets a fresh x-correlation-id on the outgoing request unless the caller
 * supplied one. Each retry is a separate request, so every attempt gets its
 * own id.
 */
export const attachCorrelationId = (config: InternalAxiosRequestConfig) => {
  if (!config.headers[CORRELATION_ID_HEADER]) {
    config.headers[CORRELATION_ID_HEADER] = newCorrelationId();
  }
  return config;
};

/**
 * Copies the request's correlation id onto the rejected error so error
 * handlers can surface it (e.g. "quote reference <id> to support").
 */
export const annotateErrorWithCorrelationId = (error: unknown) => {
  const correlationId = (error as AxiosError | undefined)?.config?.headers?.[CORRELATION_ID_HEADER];
  if (correlationId && typeof error === 'object' && error !== null) {
    (error as { correlationId?: string }).correlationId = String(correlationId);
  }
  return Promise.reject(error);
};

/**
 * Registers global axios interceptors so every REST request carries a unique
 * x-correlation-id header. The backend CorrelationIdMiddleware picks it up,
 * pushes it onto the Serilog LogContext for the request and echoes it on the
 * response, linking browser-side actions to backend log entries.
 */
export const registerCorrelationIdInterceptors = () => {
  axios.interceptors.request.use(attachCorrelationId);
  axios.interceptors.response.use(undefined, annotateErrorWithCorrelationId);
};
